/**
 * ============================================================================
 * useSidebarMenusPrefs — Preferência de menus do rail lateral (navegação)
 * ----------------------------------------------------------------------------
 * Lê e persiste os menus opcionais escolhidos em "Personalizar menu" via RPCs
 * get_user_preferences / set_user_preferences (chave `sidebar_menus` no perfil,
 * shape { optional_ids: string[] }, máximo 4 ids — validado no servidor).
 *
 * A preferência passa a acompanhar a CONTA do usuário (qualquer navegador ou
 * dispositivo). O AppShellSidebar mantém um espelho em localStorage apenas
 * como cache de primeira pintura (evita "piscar" enquanto a query carrega).
 *
 * RELEASE: 2026-09-07 v1.0.0 - Criação. Segue o mesmo padrão de
 *   useAppShortcutsPrefs (mesmo queryKey compartilhado, atualização otimista
 *   com rollback em erro).
 * ============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger condicional do módulo
const log = createLogger('useSidebarMenusPrefs');

/** Chave de cache compartilhada das preferências do usuário. */
// 2026-10-02: Chave de cache dedicada para não sobrescrever o cache de atalhos/preferências gerais.
const QUERY_KEY = (userId?: string) => ['user-preferences-sidebar', userId] as const;

/** Extrai a lista de ids opcionais do shape bruto vindo da RPC. */
function extrairIds(raw: unknown): string[] {
  if (!raw || typeof raw !== 'object') return [];
  const ids = (raw as { optional_ids?: unknown }).optional_ids;
  if (!Array.isArray(ids)) return [];
  return ids.filter((x): x is string => typeof x === 'string' && x.length > 0);
}

/** Lê os menus opcionais salvos na conta (array vazio = nunca personalizado). */
export function useSidebarMenusPrefs() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY(user?.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ sidebar_menus: string[] }> => {
      const { data, error } = await (supabase.rpc as any)('get_user_preferences');
      if (error) {
        log.error('Erro ao carregar preferência do menu lateral', { error });
        throw error;
      }
      const row = Array.isArray(data) ? data[0] : data;
      return { sidebar_menus: extrairIds(row?.sidebar_menus) };
    },
  });

  /** Persiste a nova seleção completa (atualização otimista + rollback). */
  const mutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await (supabase.rpc as any)('set_user_preferences', {
        p_prefs: { sidebar_menus: { optional_ids: ids } },
      });
      if (error) throw error;
    },
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY(user?.id) });
      const previous = queryClient.getQueryData(QUERY_KEY(user?.id));
      queryClient.setQueryData(QUERY_KEY(user?.id), (old: any) => ({
        ...(old ?? {}),
        sidebar_menus: ids,
      }));
      return { previous };
    },
    onError: (err: Error, _ids, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(QUERY_KEY(user?.id), ctx.previous);
      }
      log.error('Falha ao salvar menus da barra lateral', { error: err });
      toast.error(`Erro ao salvar menus: ${err.message}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(user?.id) });
    },
  });

  return {
    /** Ids salvos na conta; `undefined` enquanto a query não concluiu. */
    menusServidor: query.data?.sidebar_menus as string[] | undefined,
    /** True quando a preferência da conta já foi carregada. */
    isCarregado: query.isSuccess,
    /** Salva a seleção (invalida o cache ao concluir). */
    salvar: mutation.mutate,
    /** True durante a gravação. */
    salvando: mutation.isPending,
  };
}
